import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import TrophyIcon from '@mui/icons-material/EmojiEvents';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../../Services/_states';
import { getAnalyticsOrganisationId } from '../../utils/analyticsOrgScope';
import { templateReportingService } from '../../../whatsapp-templates/services/templateReportingService';
import { templateService } from '../../../whatsapp-templates/services/templateService';
import { parseTemplateListResponse } from '../../../whatsapp-templates/adapters/templateListAdapter';
import { SearchableSelect } from '../../../../components/ui';
import APIService from '../../../../Services/APIService';
import { Apis } from '../../../../Services/app.constants';
import { dateFormate, indianTimeZone } from '../../../../utils/helper.jsx';

// 6 Slots of 4 Hours (Covers 24h Day)
const SLOT_DEFS_4H = [
  { id: 0, label: '00:00 - 04:00', name: 'Late Night (12 AM - 4 AM)', startHour: 0, endHour: 4 },
  { id: 1, label: '04:00 - 08:00', name: 'Early Morning (4 AM - 8 AM)', startHour: 4, endHour: 8 },
  { id: 2, label: '08:00 - 12:00', name: 'Morning (8 AM - 12 PM)', startHour: 8, endHour: 12 },
  { id: 3, label: '12:00 - 16:00', name: 'Afternoon (12 PM - 4 PM)', startHour: 12, endHour: 16 },
  { id: 4, label: '16:00 - 20:00', name: 'Evening (4 PM - 8 PM)', startHour: 16, endHour: 20 },
  { id: 5, label: '20:00 - 24:00', name: 'Night (8 PM - 12 AM)', startHour: 20, endHour: 24 },
];

// 4 Slots of 6 Hours
const SLOT_DEFS_6H = [
  { id: 0, label: '00:00 - 06:00', name: 'Night & Early (12 AM - 6 AM)', startHour: 0, endHour: 6 },
  { id: 1, label: '06:00 - 12:00', name: 'Morning (6 AM - 12 PM)', startHour: 6, endHour: 12 },
  { id: 2, label: '12:00 - 18:00', name: 'Afternoon (12 PM - 6 PM)', startHour: 12, endHour: 18 },
  { id: 3, label: '18:00 - 24:00', name: 'Evening & Night (6 PM - 12 AM)', startHour: 18, endHour: 24 },
];

// Extract hour from sent/created timestamp in IST
function extractHourFromRecipient(r) {
  const candidates = [
    r?.sent_at,
    r?.sent_time,
    r?.created_at,
    r?.timestamp,
    r?.delivered_at,
    r?.updated_at,
  ];

  for (const c of candidates) {
    if (!c) continue;
    if (typeof c === 'string' && /^\d{1,2}:\d{2}/.test(c.trim())) {
      const parts = c.trim().split(':');
      const h = parseInt(parts[0], 10);
      if (!isNaN(h) && h >= 0 && h < 24) return h;
    }
    try {
      const ist = indianTimeZone(c);
      if (typeof ist?.hour === 'function') {
        const h = ist.hour();
        if (!isNaN(h) && h >= 0 && h < 24) return h;
      }
    } catch {
      // fallback
    }
    try {
      const d = new Date(c);
      if (!isNaN(d.getTime())) {
        return d.getHours();
      }
    } catch {
      // ignore
    }
  }
  return null;
}

// Format failure reasons cleanly
function formatFailureReasonText(val) {
  if (!val) return 'Unknown error';
  if (Array.isArray(val)) {
    return val
      .map((item) => item?.message || item?.title || (item?.code ? `Error ${item.code}` : ''))
      .filter(Boolean)
      .join(', ') || 'Failed without reason';
  }
  if (typeof val === 'object') {
    return val?.message || val?.title || (val?.code ? `Error ${val.code}` : 'Unknown error');
  }
  return String(val);
}

export default function WhatsappTemplateInsightsView({
  templateId: initialTemplateId,
  templateName,
  companyId,
  sentDate,
  availableTemplates = [],
  onClose,
  onNavigateToRoute,
  isFullPage = false,
}) {
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId || '');
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  // Timeslot configuration
  const [slotMode, setSlotMode] = useState('6_slots_4h'); // '6_slots_4h' or '4_slots_6h'
  const [slotMetricMode, setSlotMetricMode] = useState('percentage'); // 'percentage' or 'count'
  const [timeSlotView, setTimeSlotView] = useState('chart'); // 'chart' | 'table'
  const [timeSlotChartKind, setTimeSlotChartKind] = useState('bar'); // 'bar' | 'line'
  const [timeSlotMenuAnchor, setTimeSlotMenuAnchor] = useState(null);

  const userInfo = useRecoilValue(userInformationAtom);
  const effectiveCompanyId = companyId || getAnalyticsOrganisationId(userInfo);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [conversionMainView, setConversionMainView] = useState('graph'); // 'graph' | 'table'
  const [conversionGraphMode, setConversionGraphMode] = useState('count'); // 'rate' or 'count'
  const [conversionViewMode, setConversionViewMode] = useState('stages'); // 'stages' (SENT, DELIVERED, READ, NOT READ, NOT DELIVERED, NOT CLICKED), 'all' (6 cases), or 'grid' (6 individual cards)
  const [conversionChartKind, setConversionChartKind] = useState('bar'); // 'bar' | 'line'
  const [conversionMenuAnchor, setConversionMenuAnchor] = useState(null);

  const [agentMainView, setAgentMainView] = useState('graph'); // 'graph' | 'table'

  const [senderGraphMode, setSenderGraphMode] = useState('rate'); // 'rate' or 'count'
  const [agentBenchmarkType, setAgentBenchmarkType] = useState('overall'); // 'overall', 'read_rate', 'delivery_rate', 'cta_rate', 'volume'
  const [agentChartKind, setAgentChartKind] = useState('bar'); // 'bar' or 'line'
  const [agentMenuAnchor, setAgentMenuAnchor] = useState(null);

  // Filters for recipient logs
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [senderFilter, setSenderFilter] = useState('all');

  const [fetchedTemplates, setFetchedTemplates] = useState([]);

  // Auto-fetch complete template list from reporting and template services
  useEffect(() => {
    if (!effectiveCompanyId) return;
    let cancelled = false;

    async function loadAllTemplates() {
      setIsLoadingTemplates(true);
      try {
        const [repRows, listRes, legacyRes] = await Promise.allSettled([
          templateReportingService.getTemplates({ company_id: effectiveCompanyId }),
          templateService.getTemplates({
            organisation: effectiveCompanyId,
            page: 0,
            limit: 500,
            archived: 'false',
          }),
          APIService.get(`${Apis.getWhatsappTemplate}?company_id=${effectiveCompanyId}&limit=500&archived=false`),
        ]);

        if (cancelled) return;

        const mergedMap = new Map();

        // 1. Add from reporting templates (templates with activity/reports)
        if (repRows.status === 'fulfilled' && Array.isArray(repRows.value)) {
          repRows.value.forEach((t) => {
            const id = String(t.template_id || t.id || t.template_uuid || '');
            const label = t.template_name || t.name || t.label || id;
            if (id && id !== 'all') {
              mergedMap.set(id, { id, label });
            }
          });
        }

        // 2. Add from whatsapp template service
        if (listRes.status === 'fulfilled') {
          const parsed = parseTemplateListResponse(listRes.value);
          const list = parsed?.templates || listRes.value?.data?.data || listRes.value?.data || [];
          if (Array.isArray(list)) {
            list.forEach((t) => {
              const id = String(t.md_id || t.id || t.template_id || '');
              const label = t.name || t.template_name || t.label || id;
              if (id && id !== 'all' && !mergedMap.has(id)) {
                mergedMap.set(id, { id, label });
              }
            });
          }
        }

        // 3. Add from legacy endpoint
        if (legacyRes.status === 'fulfilled') {
          const body = legacyRes.value?.data?.data || legacyRes.value?.data || [];
          const list = Array.isArray(body) ? body : body.results || body.templates || [];
          if (Array.isArray(list)) {
            list.forEach((t) => {
              const id = String(t.id || t.template_id || t.template_uuid || '');
              const label = t.name || t.template_name || t.label || id;
              if (id && id !== 'all' && !mergedMap.has(id)) {
                mergedMap.set(id, { id, label });
              }
            });
          }
        }

        // 4. Also add from availableTemplates prop if passed
        availableTemplates.forEach((t) => {
          const id = String(t.id || '');
          const label = t.label || id;
          if (id && id !== 'all' && !mergedMap.has(id)) {
            mergedMap.set(id, { id, label });
          }
        });

        const sorted = Array.from(mergedMap.values()).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );

        setFetchedTemplates(sorted);
      } catch (err) {
        console.warn('Failed to load full template list', err);
      } finally {
        if (!cancelled) setIsLoadingTemplates(false);
      }
    }

    loadAllTemplates();
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyId, availableTemplates]);

  const selectOptions = useMemo(() => {
    const map = new Map();

    // 1. Add all fetchedTemplates
    fetchedTemplates.forEach((t) => {
      if (t.id && t.id !== 'all') {
        map.set(String(t.id), { id: String(t.id), label: t.label || String(t.id) });
      }
    });

    // 2. Add availableTemplates
    availableTemplates.forEach((t) => {
      if (t.id && t.id !== 'all' && !map.has(String(t.id))) {
        map.set(String(t.id), { id: String(t.id), label: t.label || String(t.id) });
      }
    });

    // 3. Ensure selectedTemplateId is present
    if (selectedTemplateId && selectedTemplateId !== 'all' && !map.has(String(selectedTemplateId))) {
      map.set(String(selectedTemplateId), {
        id: String(selectedTemplateId),
        label: templateName || String(selectedTemplateId),
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
  }, [availableTemplates, fetchedTemplates, selectedTemplateId, templateName]);

  useEffect(() => {
    if (initialTemplateId) {
      setSelectedTemplateId(initialTemplateId);
    } else if (selectOptions.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(selectOptions[0].id);
    }
  }, [initialTemplateId, selectOptions, selectedTemplateId]);

  const fetchRecipients = useCallback(async () => {
    if (!selectedTemplateId || !effectiveCompanyId) return;
    setIsLoading(true);
    setErrorMsg('');

    try {
      const params = new URLSearchParams({
        company_id: effectiveCompanyId,
        page: '0',
        limit: '150', // Fetch up to 150 recipients for high-resolution analytics
      });
      if (sentDate) {
        params.set('sent_date', sentDate);
      }

      const url = `${Apis.whatsappReportingRecipients}${encodeURIComponent(selectedTemplateId)}/recipients/?${params.toString()}`;
      const res = await APIService.get(url);

      const body = res?.data;
      const payload = body && typeof body === 'object' && 'data' in body ? body.data : body;
      const list = payload?.results || [];

      setRecipients(list);
      setTotalCount(payload?.total_count || list.length);
    } catch (err) {
      setErrorMsg(err?.message || 'Failed to fetch recipient records');
      setRecipients([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTemplateId, companyId, sentDate]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  // Overall metric totals
  const metrics = useMemo(() => {
    let sent = 0;
    let delivered = 0;
    let read = 0;
    let failed = 0;
    let ctaClicks = 0;

    recipients.forEach((r) => {
      sent += 1;
      const s = String(r?.status || '').toLowerCase();
      const isRead = s.includes('read') || Boolean(r?.read_at);
      const isDelivered = s.includes('delivered') || Boolean(r?.delivered_at) || isRead;
      const isFailed = s.includes('failed') || Boolean(r?.failure_reason || r?.failure_code);
      const hasCta = Boolean(r?.button_1 || r?.button_2 || (Number(r?.button_count_1) || 0) > 0 || (Number(r?.button_count_2) || 0) > 0);

      if (isDelivered) delivered += 1;
      if (isRead) read += 1;
      if (isFailed) failed += 1;
      if (hasCta) ctaClicks += 1;
    });

    const total = totalCount || recipients.length;
    const deliveryRate = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : '0';
    const readRate = delivered > 0 ? ((read / delivered) * 100).toFixed(1) : '0';
    const ctaCtr = delivered > 0 ? ((ctaClicks / delivered) * 100).toFixed(1) : '0';
    const failRate = sent > 0 ? ((failed / sent) * 100).toFixed(1) : '0';

    return { total, sent, delivered, read, failed, ctaClicks, deliveryRate, readRate, ctaCtr, failRate };
  }, [recipients, totalCount]);

  // Conversion Cases & Ratios (Sent vs Delivered, Sent vs Read, Delivered vs Read, Delivered vs CTA, Read vs CTA, Sent vs CTA)
  const conversionCases = useMemo(() => {
    const { sent, delivered, read, ctaClicks } = metrics;

    // 1. Sent vs Delivered (Delivery Rate)
    const sentVsDelivRate = sent > 0 ? Number(((delivered / sent) * 100).toFixed(1)) : 0;
    const sentUndelivered = Math.max(0, sent - delivered);
    const sentUndeliveredRate = sent > 0 ? Number(((sentUndelivered / sent) * 100).toFixed(1)) : 0;

    // 2. Sent vs Read (Gross Read Rate)
    const sentVsReadRate = sent > 0 ? Number(((read / sent) * 100).toFixed(1)) : 0;
    const sentUnread = Math.max(0, sent - read);
    const sentUnreadRate = sent > 0 ? Number(((sentUnread / sent) * 100).toFixed(1)) : 0;

    // 3. Delivered vs Read (Read-Through / Open Rate)
    const delivVsReadRate = delivered > 0 ? Number(((read / delivered) * 100).toFixed(1)) : 0;
    const delivUnread = Math.max(0, delivered - read);
    const delivUnreadRate = delivered > 0 ? Number(((delivUnread / delivered) * 100).toFixed(1)) : 0;

    // 4. Delivered vs CTA (Delivery to Click CTR)
    const delivVsCtaRate = delivered > 0 ? Number(((ctaClicks / delivered) * 100).toFixed(1)) : 0;
    const delivNoCta = Math.max(0, delivered - ctaClicks);
    const delivNoCtaRate = delivered > 0 ? Number(((delivNoCta / delivered) * 100).toFixed(1)) : 0;

    // 5. Read vs CTA (Click-to-Open / CTOR)
    const readVsCtaRate = read > 0 ? Number(((ctaClicks / read) * 100).toFixed(1)) : 0;
    const readNoCta = Math.max(0, read - ctaClicks);
    const readNoCtaRate = read > 0 ? Number(((readNoCta / read) * 100).toFixed(1)) : 0;

    // 6. Sent vs CTA (Overall Dispatched-to-Action)
    const sentVsCtaRate = sent > 0 ? Number(((ctaClicks / sent) * 100).toFixed(1)) : 0;
    const sentNoCta = Math.max(0, sent - ctaClicks);
    const sentNoCtaRate = sent > 0 ? Number(((sentNoCta / sent) * 100).toFixed(1)) : 0;

    const list = [
      {
        id: 'sent_vs_delivered',
        title: 'Sent v/s Delivered (Delivery Rate)',
        shortTitle: 'Sent v/s Delivered',
        rate: sentVsDelivRate,
        base: sent,
        baseLabel: 'SENT',
        target: delivered,
        targetLabel: 'DELIVERED',
        drop: sentUndelivered,
        dropRate: sentUndeliveredRate,
        dropLabel: 'NOT DELIVERED',
        benchmark: sentVsDelivRate >= 95 ? 'Excellent' : sentVsDelivRate >= 80 ? 'Good' : 'Needs Optimization',
        statusColor: sentVsDelivRate >= 95 ? '#00a884' : sentVsDelivRate >= 80 ? '#1976d2' : '#d97706',
        description: 'Delivery reliability: Percentage of dispatched messages successfully arriving on recipient phones.',
        significance: 'Measures carrier & network delivery success before reader attention is evaluated.',
      },
      {
        id: 'sent_vs_read',
        title: 'Sent v/s Read (Gross Open Rate)',
        shortTitle: 'Sent v/s Read',
        rate: sentVsReadRate,
        base: sent,
        baseLabel: 'SENT',
        target: read,
        targetLabel: 'READ',
        drop: sentUnread,
        dropRate: sentUnreadRate,
        dropLabel: 'NOT READ',
        benchmark: sentVsReadRate >= 60 ? 'Excellent' : sentVsReadRate >= 35 ? 'Good' : 'Needs Optimization',
        statusColor: sentVsReadRate >= 60 ? '#00a884' : sentVsReadRate >= 35 ? '#1976d2' : '#d97706',
        description: 'Total audience reach: Of all dispatched messages, how many were actually read by customers.',
        significance: 'Measures campaign reach against the full contacted database.',
      },
      {
        id: 'delivered_vs_read',
        title: 'Delivered v/s Read (Read-Through Rate)',
        shortTitle: 'Delivered v/s Read',
        rate: delivVsReadRate,
        base: delivered,
        baseLabel: 'DELIVERED',
        target: read,
        targetLabel: 'READ',
        drop: delivUnread,
        dropRate: delivUnreadRate,
        dropLabel: 'NOT READ',
        benchmark: delivVsReadRate >= 70 ? 'Excellent' : delivVsReadRate >= 45 ? 'Good' : 'Fair',
        statusColor: delivVsReadRate >= 70 ? '#00a884' : delivVsReadRate >= 45 ? '#1976d2' : '#d97706',
        description: 'True open rate: Of all messages arriving on phones, how many captured recipient attention.',
        significance: 'Evaluates hook line, sender branding, and notification preview impact.',
      },
      {
        id: 'delivered_vs_cta',
        title: 'Delivered v/s CTA (Delivery CTR)',
        shortTitle: 'Delivered v/s CTA',
        rate: delivVsCtaRate,
        base: delivered,
        baseLabel: 'DELIVERED',
        target: ctaClicks,
        targetLabel: 'CTA CLICKED',
        drop: delivNoCta,
        dropRate: delivNoCtaRate,
        dropLabel: 'NOT CLICKED',
        benchmark: delivVsCtaRate >= 15 ? 'High CTR' : delivVsCtaRate >= 5 ? 'Average CTR' : 'Low CTR',
        statusColor: delivVsCtaRate >= 15 ? '#00a884' : delivVsCtaRate >= 5 ? '#1976d2' : '#64748b',
        description: 'Direct response rate from delivery: Percentage of delivered recipients taking quick button action.',
        significance: 'Direct conversion metric evaluating template offer attractiveness.',
      },
      {
        id: 'read_vs_cta',
        title: 'Read v/s CTA (Click-to-Open / CTOR)',
        shortTitle: 'Read v/s CTA',
        rate: readVsCtaRate,
        base: read,
        baseLabel: 'READ',
        target: ctaClicks,
        targetLabel: 'CTA CLICKED',
        drop: readNoCta,
        dropRate: readNoCtaRate,
        dropLabel: 'NOT CLICKED',
        benchmark: readVsCtaRate >= 25 ? 'High Intent' : readVsCtaRate >= 10 ? 'Good Intent' : 'Weak Intent',
        statusColor: readVsCtaRate >= 25 ? '#00a884' : readVsCtaRate >= 10 ? '#1976d2' : '#d97706',
        description: 'Intent conversion: Of people who actually read the text, what percentage clicked the CTA button.',
        significance: 'Isolates copywriting & CTA persuasion from delivery/open friction.',
      },
      {
        id: 'sent_vs_cta',
        title: 'Sent v/s CTA (Overall Campaign Action)',
        shortTitle: 'Sent v/s CTA',
        rate: sentVsCtaRate,
        base: sent,
        baseLabel: 'SENT',
        target: ctaClicks,
        targetLabel: 'CTA CLICKED',
        drop: sentNoCta,
        dropRate: sentNoCtaRate,
        dropLabel: 'NOT CLICKED',
        benchmark: sentVsCtaRate >= 12 ? 'Outstanding' : sentVsCtaRate >= 4 ? 'Good' : 'Low',
        statusColor: sentVsCtaRate >= 12 ? '#00a884' : sentVsCtaRate >= 4 ? '#1976d2' : '#64748b',
        description: 'Macro ROI: Overall ratio of button engagements generated per template sent.',
        significance: 'Key business metric for overall broadcast conversion ROI.',
      },
    ];

    return {
      list,
      sentVsDelivered: list[0],
      sentVsRead: list[1],
      deliveredVsRead: list[2],
      deliveredVsCta: list[3],
      readVsCta: list[4],
      sentVsCta: list[5],
      summary: {
        sentVsDeliv: sentVsDelivRate,
        sentVsRead: sentVsReadRate,
        delivVsRead: delivVsReadRate,
        delivVsCta: delivVsCtaRate,
        readVsCta: readVsCtaRate,
        sentVsCta: sentVsCtaRate,
      },
    };
  }, [metrics]);

  // 6 Audience Stages: SENT, DELIVERED, READ, NOT READ, NOT DELIVERED, NOT CLICKED, CTA CLICKED
  const audienceStages = useMemo(() => {
    const { sent, delivered, read, ctaClicks } = metrics;
    const notDelivered = Math.max(0, sent - delivered);
    const notRead = Math.max(0, delivered - read);
    const notClicked = Math.max(0, read - ctaClicks);

    const sentRate = 100;
    const delivRate = sent > 0 ? Number(((delivered / sent) * 100).toFixed(1)) : 0;
    const readRateVal = sent > 0 ? Number(((read / sent) * 100).toFixed(1)) : 0;
    const notReadRateVal = sent > 0 ? Number(((notRead / sent) * 100).toFixed(1)) : 0;
    const notDelivRate = sent > 0 ? Number(((notDelivered / sent) * 100).toFixed(1)) : 0;
    const ctaRateVal = sent > 0 ? Number(((ctaClicks / sent) * 100).toFixed(1)) : 0;
    const notClickedRateVal = sent > 0 ? Number(((notClicked / sent) * 100).toFixed(1)) : 0;

    return [
      {
        id: 'sent',
        label: 'SENT',
        displayName: 'Sent (Dispatched)',
        count: sent,
        rate: sentRate,
        color: '#1976d2',
        subtitle: '100% Dispatched Base',
        description: 'Total messages dispatched to recipients',
      },
      {
        id: 'delivered',
        label: 'DELIVERED',
        displayName: 'Delivered',
        count: delivered,
        rate: delivRate,
        color: '#008d4d',
        subtitle: `${metrics.deliveryRate}% Delivery Rate`,
        description: 'Delivered successfully to recipient phones',
      },
      {
        id: 'read',
        label: 'READ',
        displayName: 'Read (Opened)',
        count: read,
        rate: readRateVal,
        color: '#7e22ce',
        subtitle: `${metrics.readRate}% Read-Through Rate`,
        description: 'Opened and read by recipients',
      },
      {
        id: 'not_read',
        label: 'NOT READ',
        displayName: 'Not Read (Unread)',
        count: notRead,
        rate: notReadRateVal,
        color: '#64748b',
        subtitle: `${delivered > 0 ? ((notRead / delivered) * 100).toFixed(1) : 0}% Ignored / Unread`,
        description: 'Delivered to phone but not opened',
      },
      {
        id: 'not_delivered',
        label: 'NOT DELIVERED',
        displayName: 'Not Delivered (Failed)',
        count: notDelivered,
        rate: notDelivRate,
        color: '#475569',
        subtitle: `${metrics.failRate}% Undelivered Drop`,
        description: 'Dispatched but undelivered or carrier failed',
      },
      {
        id: 'cta_clicked',
        label: 'CTA CLICKED',
        displayName: 'CTA Clicked',
        count: ctaClicks,
        rate: ctaRateVal,
        color: '#ea580c',
        subtitle: `${metrics.ctaCtr}% CTR from Delivered`,
        description: 'Clicked call-to-action button',
      },
      {
        id: 'not_clicked',
        label: 'NOT CLICKED',
        displayName: 'Not Clicked',
        count: notClicked,
        rate: notClickedRateVal,
        color: '#94a3b8',
        subtitle: `${read > 0 ? ((notClicked / read) * 100).toFixed(1) : 0}% Non-Click Drop`,
        description: 'Read message but did not click CTA button',
      },
    ];
  }, [metrics]);

  // Requirement: Time Slot Analysis (6 slots of 4 hrs or 4 slots of 6 hrs)
  const timeSlotAnalysis = useMemo(() => {
    const is6Slots = slotMode === '6_slots_4h';
    const defs = is6Slots ? SLOT_DEFS_4H : SLOT_DEFS_6H;
    const slotSizeHours = is6Slots ? 4 : 6;

    const slots = defs.map((d) => ({
      ...d,
      sent: 0,
      delivered: 0,
      read: 0,
      cta: 0,
      failed: 0,
    }));

    recipients.forEach((r) => {
      const hour = extractHourFromRecipient(r);
      if (hour === null || hour < 0 || hour >= 24) {
        return;
      }

      const slotIdx = Math.min(Math.floor(hour / slotSizeHours), slots.length - 1);
      const targetSlot = slots[slotIdx];
      if (!targetSlot) return;

      targetSlot.sent += 1;

      const s = String(r?.status || '').toLowerCase();
      const isRead = s.includes('read') || Boolean(r?.read_at);
      const isDelivered = s.includes('delivered') || Boolean(r?.delivered_at) || isRead;
      const isFailed = s.includes('failed') || Boolean(r?.failure_reason || r?.failure_code);
      const isCta = Boolean(
        r?.button_1 ||
        r?.button_2 ||
        (Number(r?.button_count_1) || 0) > 0 ||
        (Number(r?.button_count_2) || 0) > 0
      );

      if (isDelivered) targetSlot.delivered += 1;
      if (isRead) targetSlot.read += 1;
      if (isCta) targetSlot.cta += 1;
      if (isFailed) targetSlot.failed += 1;
    });

    const computedSlots = slots.map((s) => {
      const deliveryRate = s.sent > 0 ? Number(((s.delivered / s.sent) * 100).toFixed(1)) : 0;
      const readRate = s.sent > 0 ? Number(((s.read / s.sent) * 100).toFixed(1)) : 0;
      const ctaRate = s.sent > 0 ? Number(((s.cta / s.sent) * 100).toFixed(1)) : 0;
      const failRate = s.sent > 0 ? Number(((s.failed / s.sent) * 100).toFixed(1)) : 0;

      return {
        ...s,
        deliveryRate,
        readRate,
        ctaRate,
        failRate,
      };
    });

    // Best slot computation: prioritize read rate and cta rate
    let bestSlot = null;
    let maxScore = -1;

    computedSlots.forEach((s) => {
      if (s.sent > 0) {
        const score = (s.readRate * 1.0) + (s.ctaRate * 2.5) + (s.deliveryRate * 0.2);
        if (score > maxScore) {
          maxScore = score;
          bestSlot = s;
        }
      }
    });

    return {
      slots: computedSlots,
      labels: computedSlots.map((s) => s.label),
      names: computedSlots.map((s) => s.name),
      sentCounts: computedSlots.map((s) => s.sent),
      deliveredCounts: computedSlots.map((s) => s.delivered),
      readCounts: computedSlots.map((s) => s.read),
      ctaCounts: computedSlots.map((s) => s.cta),
      deliveryRates: computedSlots.map((s) => s.deliveryRate),
      readRates: computedSlots.map((s) => s.readRate),
      ctaRates: computedSlots.map((s) => s.ctaRate),
      bestSlot,
    };
  }, [recipients, slotMode]);

  // 24h Hourly Reading & CTA Timelines
  const readingTimelineData = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      label: `${String(i).padStart(2, '0')}:00`,
      reads: 0,
      cta: 0,
    }));

    recipients.forEach((r) => {
      const readTimestamp = r?.read_at || (r?.status === 'read' ? r?.updated_at : null);
      if (readTimestamp) {
        try {
          const ist = indianTimeZone(readTimestamp);
          const h = typeof ist?.hour === 'function' ? ist.hour() : new Date(readTimestamp).getHours();
          if (h >= 0 && h < 24) hours[h].reads += 1;
        } catch {
          // ignore
        }
      }

      const hasCta = r?.button_1 || r?.button_2 || (Number(r?.button_count_1) || 0) > 0;
      if (hasCta && r?.updated_at) {
        try {
          const ist = indianTimeZone(r.updated_at);
          const h = typeof ist?.hour === 'function' ? ist.hour() : new Date(r.updated_at).getHours();
          if (h >= 0 && h < 24) hours[h].cta += 1;
        } catch {
          // ignore
        }
      }
    });

    let peakHour = null;
    let maxReads = 0;
    hours.forEach((h) => {
      if (h.reads > maxReads) {
        maxReads = h.reads;
        peakHour = h;
      }
    });

    return {
      hours,
      labels: hours.map((h) => h.label),
      readCounts: hours.map((h) => h.reads),
      ctaCounts: hours.map((h) => h.cta),
      peakHour,
      maxReads,
    };
  }, [recipients]);

  // Failure reasons breakdown
  const failureBreakdown = useMemo(() => {
    const reasonsMap = {};

    recipients.forEach((r) => {
      if (r?.status === 'failed' || r?.failure_reason || r?.failure_code) {
        const text = formatFailureReasonText(r.failure_reason || r.failure_code);
        reasonsMap[text] = (reasonsMap[text] || 0) + 1;
      }
    });

    const entries = Object.entries(reasonsMap)
      .map(([reason, count], idx) => ({
        id: idx,
        label: reason.length > 32 ? `${reason.substring(0, 32)}…` : reason,
        fullLabel: reason,
        value: count,
      }))
      .sort((a, b) => b.value - a.value);

    return entries;
  }, [recipients]);

  // Sender distribution & Agent Benchmarking (Bulk vs Agent vs System)
  const senderBreakdown = useMemo(() => {
    const senderMap = {};

    recipients.forEach((r) => {
      const sender = r?.send_by?.trim() || 'System';
      if (!senderMap[sender]) {
        senderMap[sender] = { sent: 0, delivered: 0, read: 0, cta: 0, failed: 0 };
      }
      senderMap[sender].sent += 1;
      const s = String(r?.status || '').toLowerCase();
      const isRead = s.includes('read') || Boolean(r?.read_at);
      const isDelivered = s.includes('delivered') || Boolean(r?.delivered_at) || isRead;
      const isFailed = s.includes('failed') || Boolean(r?.failure_reason || r?.failure_code);
      const hasCta = Boolean(r?.button_1 || r?.button_2 || (Number(r?.button_count_1) || 0) > 0 || (Number(r?.button_count_2) || 0) > 0);

      if (isDelivered) senderMap[sender].delivered += 1;
      if (isRead) senderMap[sender].read += 1;
      if (isFailed) senderMap[sender].failed += 1;
      if (hasCta) senderMap[sender].cta += 1;
    });

    return Object.entries(senderMap).map(([sender, counts]) => {
      const deliveryRate = counts.sent > 0 ? Number(((counts.delivered / counts.sent) * 100).toFixed(1)) : 0;
      const readRate = counts.delivered > 0 ? Number(((counts.read / counts.delivered) * 100).toFixed(1)) : 0;
      const ctaRate = counts.delivered > 0 ? Number(((counts.cta / counts.delivered) * 100).toFixed(1)) : 0;
      const failRate = counts.sent > 0 ? Number(((counts.failed / counts.sent) * 100).toFixed(1)) : 0;

      let benchmark = 'Average';
      let benchmarkColor = '#1976d2';
      let benchmarkBg = '#eff6ff';
      if (readRate >= 60 || ctaRate >= 15) {
        benchmark = '🌟 Top Performer';
        benchmarkColor = '#00a884';
        benchmarkBg = '#ecfdf5';
      } else if (readRate >= 40) {
        benchmark = '👍 Good Engagement';
        benchmarkColor = '#0288d1';
        benchmarkBg = '#f0f9ff';
      } else if (counts.sent < 5) {
        benchmark = 'Low Activity';
        benchmarkColor = '#64748b';
        benchmarkBg = '#f1f5f9';
      } else {
        benchmark = '⚠️ Needs Attention';
        benchmarkColor = '#d97706';
        benchmarkBg = '#fffbeb';
      }

      return {
        sender,
        ...counts,
        deliveryRate,
        readRate,
        ctaRate,
        failRate,
        benchmark,
        benchmarkColor,
        benchmarkBg,
      };
    }).sort((a, b) => b.readRate - a.readRate || b.sent - a.sent);
  }, [recipients]);

  // Summary metrics for agent benchmarking leaderboard
  const senderBenchmarkSummary = useMemo(() => {
    if (!senderBreakdown.length) {
      return { totalSenders: 0, topReadAgent: null, topVolumeAgent: null, topCtaAgent: null };
    }

    const totalSenders = senderBreakdown.length;
    const sortedByRead = [...senderBreakdown].sort((a, b) => b.readRate - a.readRate || b.read - a.read);
    const topReadAgent = sortedByRead[0] || null;

    const sortedByVolume = [...senderBreakdown].sort((a, b) => b.sent - a.sent);
    const topVolumeAgent = sortedByVolume[0] || null;

    const sortedByCta = [...senderBreakdown].sort((a, b) => b.ctaRate - a.ctaRate || b.cta - a.cta);
    const topCtaAgent = sortedByCta[0]?.cta > 0 ? sortedByCta[0] : null;

    return { totalSenders, topReadAgent, topVolumeAgent, topCtaAgent };
  }, [senderBreakdown]);

  // Turnaround latency buckets
  const latencyData = useMemo(() => {
    const buckets = {
      under5m: { label: '< 5 mins', count: 0, color: '#00a884' },
      fiveTo30m: { label: '5 - 30 mins', count: 0, color: '#1976d2' },
      thirtyTo2h: { label: '30m - 2h', count: 0, color: '#9c27b0' },
      over2h: { label: '> 2 hours', count: 0, color: '#ed6c02' },
    };

    recipients.forEach((r) => {
      if (r?.delivered_at && r?.read_at) {
        const delTime = new Date(r.delivered_at).getTime();
        const readTime = new Date(r.read_at).getTime();
        if (!isNaN(delTime) && !isNaN(readTime) && readTime >= delTime) {
          const diffMins = (readTime - delTime) / (1000 * 60);
          if (diffMins < 5) buckets.under5m.count += 1;
          else if (diffMins <= 30) buckets.fiveTo30m.count += 1;
          else if (diffMins <= 120) buckets.thirtyTo2h.count += 1;
          else buckets.over2h.count += 1;
        }
      }
    });

    return Object.values(buckets);
  }, [recipients]);

  // Filtered recipient logs
  const filteredRecipients = useMemo(() => {
    return recipients.filter((r) => {
      if (statusFilter !== 'all') {
        const s = String(r?.status || '').toLowerCase();
        if (!s.includes(statusFilter)) return false;
      }
      if (senderFilter !== 'all') {
        const s = r?.send_by?.trim() || 'System';
        if (s !== senderFilter) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const contact = String(r?.contact || r?.phone_number || '').toLowerCase();
        const reason = formatFailureReasonText(r?.failure_reason).toLowerCase();
        return contact.includes(q) || reason.includes(q);
      }
      return true;
    });
  }, [recipients, statusFilter, senderFilter, searchQuery]);

  return (
    <Card
      variant="outlined"
      sx={{
        width: '100%',
        borderRadius: 3,
        bgcolor: '#ffffff',
        border: '1px solid #cbd5e1',
        boxShadow: isFullPage ? 'none' : '0 4px 16px rgba(0, 0, 0, 0.06)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header Bar */}
      <Box sx={{ p: 2.5, pb: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isFullPage && onNavigateToRoute ? (
              <IconButton onClick={onNavigateToRoute} size="small" sx={{ mr: 0.5, bgcolor: '#ffffff', border: '1px solid #cbd5e1' }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  bgcolor: '#ecfdf5',
                  color: '#00a884',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <InsightsIcon sx={{ fontSize: 24 }} />
              </Box>
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                WhatsApp Deep Template Insights
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {templateName || selectedTemplateId || 'All Templates'} · {sentDate || 'Recent Sends'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            {selectOptions.length > 0 ? (
              <Box sx={{ minWidth: { xs: 220, sm: 290 }, maxWidth: 380 }}>
                <SearchableSelect
                  size="small"
                  placeholder="Search & choose template..."
                  options={selectOptions}
                  value={
                    selectOptions.find((t) => t.id === selectedTemplateId)
                    || (selectedTemplateId ? { id: selectedTemplateId, label: templateName || selectedTemplateId } : null)
                  }
                  onChange={(opt) => {
                    if (opt?.id) {
                      setSelectedTemplateId(String(opt.id));
                    }
                  }}
                  getOptionLabel={(opt) => opt?.label || String(opt?.id || '')}
                  isOptionEqualToValue={(a, b) => String(a?.id || a) === String(b?.id || b)}
                  clearable={false}
                  loading={isLoadingTemplates}
                />
              </Box>
            ) : null}

            {/* Dedicated route button commented out per user request
            {!isFullPage && onNavigateToRoute ? (
              <Tooltip title="Open in Full Dedicated Page">
                <IconButton
                  size="small"
                  onClick={onNavigateToRoute}
                  sx={{ bgcolor: '#ffffff', border: '1px solid #cbd5e1', '&:hover': { bgcolor: '#eff6ff', color: '#1976d2' } }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            */}

            {onClose ? (
              <Tooltip title="Collapse Insights">
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{ bgcolor: '#ffffff', border: '1px solid #cbd5e1', '&:hover': { bgcolor: '#f1f5f9', color: '#475569' } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>

        {/* KPI Summary Chips */}
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 2 }}>
          <Chip label={`Total Sent: ${metrics.sent}`} sx={{ fontWeight: 700, height: 26, bgcolor: '#eff6ff', color: '#1d4ed8' }} />
          <Chip
            label={`Delivered: ${metrics.delivered} (${metrics.deliveryRate}%)`}
            variant="outlined"
            sx={{ fontWeight: 700, height: 26, color: '#00a884', borderColor: '#00a884' }}
          />
          <Chip
            label={`Read: ${metrics.read} (${metrics.readRate}%)`}
            variant="outlined"
            sx={{ fontWeight: 700, height: 26, color: '#9c27b0', borderColor: '#9c27b0' }}
          />
          <Chip
            label={`CTA Clicks: ${metrics.ctaClicks} (${metrics.ctaCtr}%)`}
            variant="outlined"
            sx={{ fontWeight: 700, height: 26, color: '#ed6c02', borderColor: '#ed6c02' }}
          />
          {metrics.failed > 0 && (
            <Chip
              label={`Failed: ${metrics.failed} (${metrics.failRate}%)`}
              variant="outlined"
              sx={{ fontWeight: 700, height: 26, color: '#64748b', borderColor: '#64748b' }}
            />
          )}
        </Stack>
      </Box>

      {/* Tabs Bar */}
      <Tabs
        value={activeTab}
        onChange={(_e, val) => setActiveTab(val)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: 2,
          borderBottom: '1px solid #e2e8f0',
          minHeight: 46,
          bgcolor: '#fafafa',
          '& .MuiTab-root': { minHeight: 46, py: 0, textTransform: 'none', fontWeight: 700, fontSize: 13 },
        }}
      >
        <Tab icon={<ScheduleIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Sending Time Slots & Best Window" />
        <Tab icon={<CompareArrowsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Sent, Read & Delivered Conversion (Graph & Table)" />
        <Tab icon={<ErrorOutlineIcon sx={{ fontSize: 17 }} />} iconPosition="start" label={`Failures (${metrics.failed})`} />
        <Tab icon={<PersonOutlineIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Sender & Agent Benchmark (Graph & Table)" />
        <Tab icon={<TableChartOutlinedIcon sx={{ fontSize: 17 }} />} iconPosition="start" label={`Recipient Logs (${recipients.length})`} />
      </Tabs>

      {/* Tab Panels Body */}
      <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1, minHeight: 420 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
            <CircularProgress size={40} sx={{ color: '#00a884', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Analyzing recipient events & performance metrics...
            </Typography>
          </Box>
        ) : errorMsg ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="error" variant="body1" sx={{ fontWeight: 600 }}>
              {errorMsg}
            </Typography>
          </Box>
        ) : !recipients.length ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">
              No recipient data available for this template/date selection.
            </Typography>
          </Box>
        ) : (
          <>
            {/* TAB 0: Sending Time Slots & Best Window */}
            {activeTab === 0 && (
              <Stack spacing={3}>
                {/* Best Slot Banner */}
                {timeSlotAnalysis.bestSlot && timeSlotAnalysis.bestSlot.sent > 0 ? (
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 50%, #eff6ff 100%)',
                      border: '1px solid #a7f3d0',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.08)',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: '50%',
                          bgcolor: '#d1fae5',
                          color: '#059669',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <TrophyIcon sx={{ fontSize: 32, color: '#059669' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#065f46', fontSize: 16 }}>
                            🌟 Best Time Slot to Send: {timeSlotAnalysis.bestSlot.name} ({timeSlotAnalysis.bestSlot.label})
                          </Typography>
                          <Chip
                            size="small"
                            label="Peak Engagement Window"
                            sx={{ bgcolor: '#059669', color: '#fff', fontWeight: 700, height: 22, fontSize: 11 }}
                          />
                        </Stack>

                        <Typography variant="body2" sx={{ color: '#047857', mb: 1.5, lineHeight: 1.5 }}>
                          Templates dispatched during <strong>{timeSlotAnalysis.bestSlot.label}</strong> achieve peak engagement with{' '}
                          <strong>{timeSlotAnalysis.bestSlot.readRate}%</strong> read rate and{' '}
                          <strong>{timeSlotAnalysis.bestSlot.ctaRate}%</strong> CTA button clicks! Schedule your major campaigns in this window for maximum conversions.
                        </Typography>

                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={`Read Rate: ${timeSlotAnalysis.bestSlot.readRate}%`}
                            sx={{ fontWeight: 700, bgcolor: '#f3e8ff', color: '#7e22ce', borderColor: '#d8b4fe' }}
                            variant="outlined"
                          />
                          <Chip
                            label={`CTA Click Rate: ${timeSlotAnalysis.bestSlot.ctaRate}%`}
                            sx={{ fontWeight: 700, bgcolor: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }}
                            variant="outlined"
                          />
                          <Chip
                            label={`Delivery Rate: ${timeSlotAnalysis.bestSlot.deliveryRate}%`}
                            sx={{ fontWeight: 700, bgcolor: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' }}
                            variant="outlined"
                          />
                          <Chip
                            label={`Volume: ${timeSlotAnalysis.bestSlot.sent} messages sent`}
                            sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                ) : null}

                {/* Slots Chart & Table */}
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 2 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        📊 Sending Time Slots (Delivery %, Read % & CTA Click %)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Breakdown of delivery rate, read rate, and CTA button clicks based on what time templates were sent.
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <ToggleButtonGroup
                        size="small"
                        value={timeSlotView}
                        exclusive
                        onChange={(_e, val) => val && setTimeSlotView(val)}
                        sx={{ height: 30 }}
                      >
                        <ToggleButton value="chart" sx={{ px: 1.5, py: 0, fontSize: 11, fontWeight: 700, gap: 0.5 }}>
                          <BarChartIcon sx={{ fontSize: 15 }} /> Chart
                        </ToggleButton>
                        <ToggleButton value="table" sx={{ px: 1.5, py: 0, fontSize: 11, fontWeight: 700, gap: 0.5 }}>
                          <TableChartOutlinedIcon sx={{ fontSize: 15 }} /> Table
                        </ToggleButton>
                      </ToggleButtonGroup>

                      {/* 3-Dot Menu for Time Slots */}
                      <IconButton
                        size="small"
                        onClick={(e) => setTimeSlotMenuAnchor(e.currentTarget)}
                        sx={{ border: '1px solid #cbd5e1' }}
                        aria-label="Time slots options"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={timeSlotMenuAnchor}
                        open={Boolean(timeSlotMenuAnchor)}
                        onClose={() => setTimeSlotMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        MenuListProps={{ sx: { py: 0.5, minWidth: 200 } }}
                      >
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                          TIME SLOTS
                        </Typography>
                        <MenuItem
                          selected={slotMode === '6_slots_4h'}
                          onClick={() => { setSlotMode('6_slots_4h'); setTimeSlotMenuAnchor(null); }}
                        >
                          6 Slots (4 hours each)
                        </MenuItem>
                        <MenuItem
                          selected={slotMode === '4_slots_6h'}
                          onClick={() => { setSlotMode('4_slots_6h'); setTimeSlotMenuAnchor(null); }}
                        >
                          4 Slots (6 hours each)
                        </MenuItem>

                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                          METRIC MODE
                        </Typography>
                        <MenuItem
                          selected={slotMetricMode === 'percentage'}
                          onClick={() => { setSlotMetricMode('percentage'); setTimeSlotMenuAnchor(null); }}
                        >
                          Rate (%)
                        </MenuItem>
                        <MenuItem
                          selected={slotMetricMode === 'count'}
                          onClick={() => { setSlotMetricMode('count'); setTimeSlotMenuAnchor(null); }}
                        >
                          Count (#)
                        </MenuItem>

                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                          CHART TYPE
                        </Typography>
                        <MenuItem
                          selected={timeSlotChartKind === 'bar'}
                          onClick={() => { setTimeSlotChartKind('bar'); setTimeSlotView('chart'); setTimeSlotMenuAnchor(null); }}
                        >
                          <BarChartIcon sx={{ fontSize: 16, mr: 1, color: '#1976d2' }} /> Bar Chart
                        </MenuItem>
                        <MenuItem
                          selected={timeSlotChartKind === 'line'}
                          onClick={() => { setTimeSlotChartKind('line'); setTimeSlotView('chart'); setTimeSlotMenuAnchor(null); }}
                        >
                          <ShowChartIcon sx={{ fontSize: 16, mr: 1, color: '#00a884' }} /> Line Chart
                        </MenuItem>
                      </Menu>
                    </Stack>
                  </Stack>

                  {timeSlotView === 'chart' ? (
                  /* Grouped Bar Chart */
                  <Box sx={{ height: 280, width: '100%', mt: 1 }}>
                    <BarChart
                      height={270}
                      borderRadius={4}
                      barLabel={(item) =>
                        item?.value != null
                          ? slotMetricMode === 'percentage'
                            ? `${Number(item.value).toFixed(0)}%`
                            : `${Number(item.value).toLocaleString()}`
                          : ''
                      }
                      xAxis={[
                        {
                          scaleType: 'band',
                          data: timeSlotAnalysis.labels,
                          tickLabelStyle: { fontSize: 11, fontWeight: 600 },
                        },
                      ]}
                      yAxis={
                        slotMetricMode === 'percentage'
                          ? [{ max: 100, valueFormatter: (v) => `${v}%` }]
                          : undefined
                      }
                      series={
                        slotMetricMode === 'percentage'
                          ? [
                              {
                                data: timeSlotAnalysis.deliveryRates,
                                label: 'Delivered %',
                                color: '#00a884',
                                valueFormatter: (v) => `${v}%`,
                              },
                              {
                                data: timeSlotAnalysis.readRates,
                                label: 'Read %',
                                color: '#9c27b0',
                                valueFormatter: (v) => `${v}%`,
                              },
                              {
                                data: timeSlotAnalysis.ctaRates,
                                label: 'CTA Click %',
                                color: '#ed6c02',
                                valueFormatter: (v) => `${v}%`,
                              },
                            ]
                          : [
                              {
                                data: timeSlotAnalysis.sentCounts,
                                label: 'Sent',
                                color: '#1976d2',
                              },
                              {
                                data: timeSlotAnalysis.deliveredCounts,
                                label: 'Delivered',
                                color: '#00a884',
                              },
                              {
                                data: timeSlotAnalysis.readCounts,
                                label: 'Read',
                                color: '#9c27b0',
                              },
                              {
                                data: timeSlotAnalysis.ctaCounts,
                                label: 'CTA Clicks',
                                color: '#ed6c02',
                              },
                            ]
                      }
                      margin={{ top: 20, right: 20, bottom: 40, left: 45 }}
                      sx={{
                        '& .MuiBarLabel-root': {
                          fill: '#ffffff',
                          fontSize: 10,
                          fontWeight: 700,
                          textShadow: '0 1px 2px rgba(0,0,0,0.85)',
                          pointerEvents: 'none',
                        },
                      }}
                    />
                  </Box>
                  ) : (
                  /* Detailed Table Matrix (MUI TableContainer) */
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2.5, borderRadius: 2, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.25, px: 1.5 }}>Time Slot</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.25, px: 1.5 }}>Window</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.25, px: 1.5 }}>Sent</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.25, px: 1.5 }}>Delivered Rate %</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.25, px: 1.5 }}>Read Rate %</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.25, px: 1.5 }}>CTA Click %</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.25, px: 1.5 }}>Performance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timeSlotAnalysis.slots.map((slot) => {
                          const isBest = timeSlotAnalysis.bestSlot?.id === slot.id && slot.sent > 0;
                          return (
                            <TableRow
                              hover
                              key={slot.id}
                              sx={{
                                bgcolor: isBest ? '#f0fdf4' : 'inherit',
                                '& td': { py: 1.25, px: 1.5, borderBottom: '1px solid #f1f5f9' },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 700 }}>{slot.label}</TableCell>
                              <TableCell sx={{ color: '#334155' }}>{slot.name}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{slot.sent}</TableCell>
                              <TableCell sx={{ color: '#00a884', fontWeight: 600 }}>
                                {slot.deliveryRate}% <span style={{ color: '#64748b', fontSize: 11 }}>({slot.delivered})</span>
                              </TableCell>
                              <TableCell sx={{ color: '#9c27b0', fontWeight: 700 }}>
                                {slot.readRate}% <span style={{ color: '#64748b', fontSize: 11 }}>({slot.read})</span>
                              </TableCell>
                              <TableCell sx={{ color: '#ed6c02', fontWeight: 700 }}>
                                {slot.ctaRate}% <span style={{ color: '#64748b', fontSize: 11 }}>({slot.cta})</span>
                              </TableCell>
                              <TableCell>
                                {isBest ? (
                                  <Chip
                                    size="small"
                                    icon={<TrophyIcon style={{ fontSize: 13, color: '#059669' }} />}
                                    label="🌟 Best Window"
                                    sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 700, height: 22 }}
                                  />
                                ) : slot.sent === 0 ? (
                                  <Chip size="small" label="No Sends" sx={{ height: 20, fontSize: 10, color: '#94a3b8' }} />
                                ) : slot.readRate >= 40 ? (
                                  <Chip
                                    size="small"
                                    label="🔥 High"
                                    sx={{ bgcolor: '#f3e8ff', color: '#7e22ce', fontWeight: 700, height: 20, fontSize: 10 }}
                                  />
                                ) : slot.readRate >= 20 ? (
                                  <Chip
                                    size="small"
                                    label="👍 Moderate"
                                    sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, height: 20, fontSize: 10 }}
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label="⚠️ Low"
                                    sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600, height: 20, fontSize: 10 }}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}
                </Card>

                {/* 24h Peak Reading Hours */}
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        🕒 Peak Reading Hours Timeline (Point 14)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Hourly distribution of when recipients open and read this template.
                      </Typography>
                    </Box>
                    {readingTimelineData.peakHour && readingTimelineData.maxReads > 0 ? (
                      <Chip
                        size="small"
                        color="secondary"
                        label={`🔥 Peak Reading: ${readingTimelineData.peakHour.label} (${readingTimelineData.maxReads} reads)`}
                        sx={{ fontWeight: 700 }}
                      />
                    ) : null}
                  </Stack>

                  <Box sx={{ height: 260, width: '100%', mt: 1 }}>
                    <BarChart
                      height={250}
                      borderRadius={4}
                      xAxis={[
                        {
                          scaleType: 'band',
                          data: readingTimelineData.labels,
                          tickLabelStyle: { fontSize: 10 },
                          tickLabelInterval: (_val, index) => index % 2 === 0,
                        },
                      ]}
                      series={[
                        {
                          data: readingTimelineData.readCounts,
                          label: 'Messages Read',
                          color: '#9c27b0',
                        },
                        {
                          data: readingTimelineData.ctaCounts,
                          label: 'CTA Clicks',
                          color: '#ed6c02',
                        },
                      ]}
                      margin={{ top: 10, right: 20, bottom: 35, left: 35 }}
                    />
                  </Box>
                </Card>

                {/* Read Response Latency */}
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    ⚡ Turnaround / Read Response Speed
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    How quickly users open the template after delivery.
                  </Typography>

                  <Grid container spacing={2}>
                    {latencyData.map((b) => (
                      <Grid size={{ xs: 6, sm: 3 }} key={b.label}>
                        <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: b.color }}>
                            {b.count}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {b.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Stack>
            )}

            {/* TAB 1: Conversion & Comparative Cases (Sent vs Delivered, Sent vs Read, Delivered vs Read, etc.) */}
            {activeTab === 1 && (
              <Stack spacing={3}>
                {/* 1. Step-by-Step Audience Conversion Journey (Prominently at the TOP) */}
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        🎯 Step-by-Step Audience Conversion Journey
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Comprehensive progression tracking from initial outreach dispatch to customer button conversion.
                      </Typography>
                    </Box>
                    <Chip
                      icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                      label={`Audience Retention: ${metrics.sent > 0 ? ((metrics.read / metrics.sent) * 100).toFixed(1) : 0}% Read | ${metrics.ctaCtr}% CTR`}
                      sx={{ bgcolor: '#ecfdf5', color: '#047857', fontWeight: 700, fontSize: 11 }}
                    />
                  </Stack>

                  {/* Summary Highlight Banner */}
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, mb: 2.5 }}>
                    <Typography variant="body2" sx={{ color: '#1e293b', lineHeight: 1.6 }}>
                      Out of <strong>{metrics.sent.toLocaleString()}</strong> messages dispatched, <strong>{metrics.delivered.toLocaleString()}</strong> were delivered (<strong>{metrics.deliveryRate}%</strong>), <strong>{metrics.read.toLocaleString()}</strong> were read (<strong>{metrics.readRate}%</strong> of delivered · <strong>{metrics.sent > 0 ? ((metrics.read / metrics.sent) * 100).toFixed(1) : 0}%</strong> of sent), and <strong>{metrics.ctaClicks.toLocaleString()}</strong> triggered CTA button clicks (<strong>{metrics.ctaCtr}%</strong> delivery CTR · <strong>{conversionCases?.readVsCta?.rate ?? conversionCases?.summary?.readVsCta ?? 0}%</strong> CTOR).
                    </Typography>
                  </Box>

                  {/* 4 Interactive Funnel Stages */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Stage 1: Dispatched */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ p: 2, border: '1px solid #bfdbfe', borderRadius: 2, bgcolor: '#eff6ff', height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: '#1d4ed8' }}>1. DISPATCHED</Typography>
                          <SendIcon sx={{ fontSize: 18, color: '#1d4ed8' }} />
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e3a8a' }}>
                          {metrics.sent.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600, display: 'block', mt: 0.5 }}>
                          100% Total Outreach
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Stage 2: Delivered */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ p: 2, border: '1px solid #a7f3d0', borderRadius: 2, bgcolor: '#ecfdf5', height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: '#047857' }}>2. DELIVERED</Typography>
                          <DoneAllIcon sx={{ fontSize: 18, color: '#047857' }} />
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#064e3b' }}>
                          {metrics.delivered.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, display: 'block', mt: 0.5 }}>
                          {metrics.deliveryRate}% Delivery Rate ({metrics.sent - metrics.delivered} undelivered)
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Stage 3: Read */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ p: 2, border: '1px solid #e9d5ff', borderRadius: 2, bgcolor: '#faf5ff', height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: '#7e22ce' }}>3. READ / OPENED</Typography>
                          <VisibilityIcon sx={{ fontSize: 18, color: '#7e22ce' }} />
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#581c87' }}>
                          {metrics.read.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9333ea', fontWeight: 700, display: 'block', mt: 0.5 }}>
                          {metrics.readRate}% Read-Through ({metrics.delivered - metrics.read} unread)
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Stage 4: CTA Action */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ p: 2, border: '1px solid #fed7aa', borderRadius: 2, bgcolor: '#fff7ed', height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: '#c2410c' }}>4. CTA CLICKED</Typography>
                          <TouchAppIcon sx={{ fontSize: 18, color: '#c2410c' }} />
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#7c2d12' }}>
                          {metrics.ctaClicks.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#ea580c', fontWeight: 700, display: 'block', mt: 0.5 }}>
                          {metrics.ctaCtr}% CTR ({conversionCases?.readVsCta?.rate ?? conversionCases?.summary?.readVsCta ?? 0}% of readers)
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Funnel Bar Chart with Counts inside bars */}
                  <Box sx={{ height: 260, width: '100%', mt: 2 }}>
                    <BarChart
                      height={250}
                      borderRadius={6}
                      barLabel={(item) => (item?.value != null ? `${Number(item.value).toLocaleString()}` : '')}
                      xAxis={[
                        {
                          scaleType: 'band',
                          data: ['1. Dispatched', '2. Delivered', '3. Read', '4. CTA Clicked'],
                          tickLabelStyle: { fontSize: 12, fontWeight: 700 },
                        },
                      ]}
                      series={[
                        {
                          data: [metrics.sent, metrics.delivered, metrics.read, metrics.ctaClicks],
                          label: 'Recipients Count',
                          color: '#008d4d',
                        },
                      ]}
                      margin={{ top: 20, right: 20, bottom: 35, left: 45 }}
                      sx={{
                        '& .MuiBarLabel-root': {
                          fill: '#ffffff',
                          fontSize: 12,
                          fontWeight: 700,
                          textShadow: '0 1px 2px rgba(0,0,0,0.85)',
                          pointerEvents: 'none',
                        },
                      }}
                    />
                  </Box>
                </Card>

                {/* 2. Sent, Delivered, Read & CTA Comparison Graph (All-in-One OR 6 Individual Case Graphs Grid) */}
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 2 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        📊 Sent, Delivered, Read & CTA Comparison Graphs
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Compare conversion rates, volume counts, and drop-offs across SENT, DELIVERED, READ, NOT READ, NOT DELIVERED, NOT CLICKED.
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <ToggleButtonGroup
                        size="small"
                        value={conversionMainView}
                        exclusive
                        onChange={(_e, val) => val && setConversionMainView(val)}
                        sx={{ height: 30 }}
                      >
                        <ToggleButton value="graph" sx={{ px: 1.5, py: 0, fontSize: 11, fontWeight: 700, gap: 0.5 }}>
                          <BarChartIcon sx={{ fontSize: 15 }} /> Graph
                        </ToggleButton>
                        <ToggleButton value="table" sx={{ px: 1.5, py: 0, fontSize: 11, fontWeight: 700, gap: 0.5 }}>
                          <TableChartOutlinedIcon sx={{ fontSize: 15 }} /> Table
                        </ToggleButton>
                      </ToggleButtonGroup>

                      {/* 3-Dot Menu for Conversion Graph */}
                      <IconButton
                        size="small"
                        onClick={(e) => setConversionMenuAnchor(e.currentTarget)}
                        sx={{ border: '1px solid #cbd5e1' }}
                        aria-label="Conversion graph options"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={conversionMenuAnchor}
                        open={Boolean(conversionMenuAnchor)}
                        onClose={() => setConversionMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        MenuListProps={{ sx: { py: 0.5, minWidth: 230 } }}
                      >
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                          GRAPH VIEW
                        </Typography>
                        <MenuItem
                          selected={conversionViewMode === 'stages'}
                          onClick={() => {
                            setConversionViewMode('stages');
                            setConversionMainView('graph');
                            setConversionMenuAnchor(null);
                          }}
                        >
                          <BarChartIcon sx={{ fontSize: 18, mr: 1, color: '#1976d2' }} />
                          Audience Stages Funnel
                        </MenuItem>
                        <MenuItem
                          selected={conversionViewMode === 'all'}
                          onClick={() => {
                            setConversionViewMode('all');
                            setConversionMainView('graph');
                            setConversionMenuAnchor(null);
                          }}
                        >
                          <CompareArrowsIcon sx={{ fontSize: 18, mr: 1, color: '#00a884' }} />
                          6 Conversion Cases
                        </MenuItem>
                        <MenuItem
                          selected={conversionViewMode === 'grid'}
                          onClick={() => {
                            setConversionViewMode('grid');
                            setConversionMainView('graph');
                            setConversionMenuAnchor(null);
                          }}
                        >
                          <ShowChartIcon sx={{ fontSize: 18, mr: 1, color: '#7e22ce' }} />
                          6 Individual Cards Grid
                        </MenuItem>

                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                          DISPLAY MODE
                        </Typography>
                        <MenuItem
                          selected={conversionGraphMode === 'count'}
                          onClick={() => {
                            setConversionGraphMode('count');
                            setConversionMenuAnchor(null);
                          }}
                        >
                          Volume (#)
                        </MenuItem>
                        <MenuItem
                          selected={conversionGraphMode === 'rate'}
                          onClick={() => {
                            setConversionGraphMode('rate');
                            setConversionMenuAnchor(null);
                          }}
                        >
                          Rate (%)
                        </MenuItem>
                      </Menu>
                    </Stack>
                  </Stack>

                  {conversionMainView === 'table' ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, borderRadius: 2, overflow: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Stage / Milestone</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Recipients (Count)</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Conversion Rate</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Drop-off / Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {audienceStages.map((st) => (
                            <TableRow hover key={st.id} sx={{ '& td': { py: 1.25, px: 1.5, borderBottom: '1px solid #f1f5f9' } }}>
                              <TableCell sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color, flexShrink: 0 }} />
                                {st.label}
                              </TableCell>
                              <TableCell sx={{ color: '#475569', fontWeight: 600 }}>
                                {st.id.includes('not') ? (
                                  <Chip size="small" label="Drop-off" sx={{ bgcolor: '#f1f5f9', color: '#64748b', height: 20, fontSize: 10 }} />
                                ) : st.id === 'cta' ? (
                                  <Chip size="small" label="High Intent" sx={{ bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 700, height: 20, fontSize: 10 }} />
                                ) : (
                                  <Chip size="small" label="Milestone" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, height: 20, fontSize: 10 }} />
                                )}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{Number(st.count).toLocaleString()}</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: st.color }}>{st.rate}% of sent</TableCell>
                              <TableCell sx={{ color: '#64748b', fontSize: 12 }}>{st.subtitle}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <>
                    {/* Mode A: Audience Stages Breakdown (SENT, DELIVERED, READ, NOT READ, NOT DELIVERED, CTA CLICKED, NOT CLICKED) */}
                  {conversionViewMode === 'stages' && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ height: 320, width: '100%' }}>
                        <BarChart
                          height={310}
                          borderRadius={5}
                          barLabel={(item) =>
                            item?.value != null
                              ? conversionGraphMode === 'rate'
                                ? `${Number(item.value).toFixed(1)}%`
                                : `${Number(item.value).toLocaleString()}`
                              : ''
                          }
                          xAxis={[
                            {
                              scaleType: 'band',
                              data: ['Audience Stages'],
                              tickLabelStyle: { display: 'none' },
                            },
                          ]}
                          slotProps={{ legend: { hidden: true } }}
                          yAxis={
                            conversionGraphMode === 'rate'
                              ? [{ max: 100, valueFormatter: (v) => `${v}%` }]
                              : undefined
                          }
                          series={audienceStages.map((s) => ({
                            id: s.id,
                            label: s.label,
                            data: [conversionGraphMode === 'rate' ? s.rate : s.count],
                            color: s.color,
                            valueFormatter: (v) =>
                              conversionGraphMode === 'rate'
                                ? `${v}% of sent (${s.subtitle})`
                                : `${Number(v).toLocaleString()} recipients (${s.subtitle})`,
                          }))}
                          margin={{ top: 25, right: 20, bottom: 25, left: 50 }}
                          sx={{
                            '& .MuiBarLabel-root': {
                              fill: '#ffffff',
                              fontSize: 11,
                              fontWeight: 800,
                              textShadow: '0 1px 2px rgba(0,0,0,0.9)',
                              pointerEvents: 'none',
                            },
                          }}
                        />
                      </Box>

                      {/* Stage Indicator Cards Grid */}
                      <Grid container spacing={1.5} sx={{ mt: 2 }}>
                        {audienceStages.map((st) => (
                          <Grid size={{ xs: 6, sm: 4, md: 12 / 7 }} key={st.id}>
                            <Box
                              sx={{
                                p: 1.5,
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                borderTop: `3px solid ${st.color}`,
                                bgcolor: '#ffffff',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: st.color, display: 'block', fontSize: 11, letterSpacing: 0.5 }}>
                                  {st.label}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2, my: 0.5 }}>
                                  {st.count.toLocaleString()}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: st.color, fontWeight: 700, fontSize: 11, display: 'block' }}>
                                  {st.rate}% of sent
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
                                  {st.subtitle}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Mode B: All-in-One 6 Cases Graph */}
                  {conversionViewMode === 'all' && (
                    <Box sx={{ height: 300, width: '100%', mt: 1 }}>
                      <BarChart
                        height={290}
                        borderRadius={5}
                        barLabel={(item) =>
                          item?.value != null
                            ? conversionGraphMode === 'rate'
                              ? `${Number(item.value).toFixed(0)}%`
                              : `${Number(item.value).toLocaleString()}`
                            : ''
                        }
                        xAxis={[
                          {
                            scaleType: 'band',
                            data: conversionCases.list.map((c) => c.shortTitle),
                            tickLabelStyle: { fontSize: 11, fontWeight: 700 },
                          },
                        ]}
                        yAxis={
                          conversionGraphMode === 'rate'
                            ? [{ max: 100, valueFormatter: (v) => `${v}%` }]
                            : undefined
                        }
                        series={
                          conversionGraphMode === 'rate'
                            ? [
                                {
                                  data: conversionCases.list.map((c) => c.rate),
                                  label: 'Conversion %',
                                  color: '#00a884',
                                  valueFormatter: (v) => `${v}%`,
                                },
                                {
                                  data: conversionCases.list.map((c) => c.dropRate),
                                  label: 'Drop-off %',
                                  color: '#64748b',
                                  valueFormatter: (v) => `${v}%`,
                                },
                              ]
                            : [
                                {
                                  data: conversionCases.list.map((c) => c.base),
                                  label: 'Base (SENT / DELIVERED / READ)',
                                  color: '#1976d2',
                                },
                                {
                                  data: conversionCases.list.map((c) => c.target),
                                  label: 'Converted (DELIVERED / READ / CLICKED)',
                                  color: '#00a884',
                                },
                                {
                                  data: conversionCases.list.map((c) => c.drop),
                                  label: 'Drop-off (NOT DELIVERED / NOT READ / NOT CLICKED)',
                                  color: '#64748b',
                                },
                              ]
                        }
                        margin={{ top: 20, right: 20, bottom: 45, left: 45 }}
                        sx={{
                          '& .MuiBarLabel-root': {
                            fill: '#ffffff',
                            fontSize: 10,
                            fontWeight: 700,
                            textShadow: '0 1px 2px rgba(0,0,0,0.85)',
                            pointerEvents: 'none',
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Mode C: 6 Individual Case Graphs Grid */}
                  {conversionViewMode === 'grid' && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {conversionCases.list.map((c) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={`case-card-${c.id}`}>
                          <Card variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', bgcolor: '#ffffff' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                {c.title}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${c.rate}%`}
                                sx={{ fontWeight: 800, bgcolor: `${c.statusColor}18`, color: c.statusColor, fontSize: 11 }}
                              />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {c.description}
                            </Typography>

                            <Box sx={{ height: 180, width: '100%' }}>
                              <BarChart
                                height={175}
                                borderRadius={4}
                                barLabel={(item) =>
                                  item?.value != null ? `${Number(item.value).toLocaleString()}` : ''
                                }
                                xAxis={[
                                  {
                                    scaleType: 'band',
                                    data: [c.baseLabel, c.targetLabel, c.dropLabel],
                                    tickLabelStyle: { fontSize: 10, fontWeight: 700 },
                                  },
                                ]}
                                series={[
                                  {
                                    data: [c.base, c.target, c.drop],
                                    color: c.statusColor,
                                  },
                                ]}
                                margin={{ top: 15, right: 10, bottom: 25, left: 35 }}
                                sx={{
                                  '& .MuiBarLabel-root': {
                                    fill: '#ffffff',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    textShadow: '0 1px 2px rgba(0,0,0,0.85)',
                                    pointerEvents: 'none',
                                  },
                                }}
                              />
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                    </>
                  )}
                </Card>

                {/* 2. Detailed Conversion Comparison Table */}
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                    📋 Conversion Comparison Matrix Table
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Comprehensive metrics for Sent, Delivered, Read, and CTA click ratios with benchmark standards.
                  </Typography>

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Comparison Stage Pair</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Base Stage (SENT / DELIVERED / READ)</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Converted Stage (DELIVERED / READ / CLICKED)</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Conversion Rate (%)</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Drop-off (NOT DELIVERED / NOT READ / NOT CLICKED)</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Benchmark Rating</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Significance & Action Insight</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {conversionCases.list.map((c) => (
                          <TableRow hover key={c.id}>
                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>{c.title}</Typography>
                              <Typography variant="caption" color="text.secondary">{c.description}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap', color: '#1e293b', fontWeight: 600 }}>
                              {c.base.toLocaleString()} <Typography component="span" variant="caption" color="text.secondary">({c.baseLabel})</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap', color: '#00a884', fontWeight: 700 }}>
                              {c.target.toLocaleString()} <Typography component="span" variant="caption" color="text.secondary">({c.targetLabel})</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                              <Stack spacing={0.5} sx={{ minWidth: 110 }}>
                                <Typography variant="body2" sx={{ fontWeight: 800, color: c.statusColor }}>
                                  {c.rate}%
                                </Typography>
                                <Box sx={{ width: '100%', bgcolor: '#e2e8f0', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                  <Box sx={{ width: `${Math.min(100, c.rate)}%`, bgcolor: c.statusColor, height: '100%', borderRadius: 3 }} />
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap', color: '#64748b' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.drop.toLocaleString()} ({c.dropRate}%)</Typography>
                              <Typography variant="caption" color="text.secondary">{c.dropLabel}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                              <Chip size="small" label={c.benchmark} sx={{ fontWeight: 700, bgcolor: `${c.statusColor}18`, color: c.statusColor }} />
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: 12, color: '#475569' }}>
                              {c.significance}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>


                {/* 4. Conversion Scenario KPI Cards Grid */}
                <Grid container spacing={2.5}>
                  {conversionCases.list.map((c) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={c.id}>
                      <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, height: '100%', border: '1px solid #cbd5e1' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            {c.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={c.benchmark}
                            sx={{ fontWeight: 700, bgcolor: `${c.statusColor}18`, color: c.statusColor }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                          {c.description}
                        </Typography>

                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="caption" color="text.secondary">Conversion Rate</Typography>
                              <Typography variant="h4" sx={{ fontWeight: 800, color: c.statusColor }}>
                                {c.rate}%
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                {c.target.toLocaleString()} / {c.base.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({c.drop.toLocaleString()} {c.dropLabel.toLowerCase()} · {c.dropRate}%)
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            )}

            {/* TAB 2: Failures Breakdown */}
            {activeTab === 2 && (
              <Stack spacing={2.5}>
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    ⚠️ Failure Reasons & Error Analysis
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Reasons why WhatsApp messages failed to deliver to recipients.
                  </Typography>

                  {!failureBreakdown.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      No failed messages found in this batch! 🎉
                    </Typography>
                  ) : (
                    <Grid container spacing={3} alignItems="center">
                      <Grid size={{ xs: 12, md: 5 }}>
                        <PieChart
                          height={220}
                          series={[
                            {
                              data: failureBreakdown,
                              innerRadius: 35,
                              outerRadius: 85,
                              paddingAngle: 2,
                            },
                          ]}
                          hideLegend
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 7 }}>
                        <Stack spacing={1}>
                          {failureBreakdown.map((item) => (
                            <Box
                              key={item.id}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1,
                                bgcolor: '#fef2f2',
                                borderRadius: 1.5,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#991b1b', pr: 1 }}>
                                {item.fullLabel}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${item.value} (${((item.value / metrics.failed) * 100).toFixed(0)}%)`}
                                sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700 }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  )}
                </Card>
              </Stack>
            )}

            {/* TAB 3: Sender & Agent Benchmark (Graph & Table) */}
            {activeTab === 3 && (
              <Stack spacing={3}>
                {/* 1. Benchmark KPI & Leaderboard Cards */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', bgcolor: '#f8fafc' }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#e0f2fe', color: '#0369a1', display: 'flex' }}>
                          <PersonOutlineIcon />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            Active Senders / Agents
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            {senderBenchmarkSummary.totalSenders}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        height: '100%',
                        bgcolor: senderBenchmarkSummary.topReadAgent ? '#ecfdf5' : '#f8fafc',
                        borderColor: senderBenchmarkSummary.topReadAgent ? '#a7f3d0' : undefined,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#d1fae5', color: '#059669', display: 'flex' }}>
                          <TrophyIcon />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" sx={{ color: '#047857', fontWeight: 700 }}>
                            Top Read-Through Agent
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#065f46', noWrap: true }}>
                            {senderBenchmarkSummary.topReadAgent?.sender || 'N/A'}
                          </Typography>
                          {senderBenchmarkSummary.topReadAgent && (
                            <Typography variant="caption" sx={{ color: '#047857', fontWeight: 700 }}>
                              {senderBenchmarkSummary.topReadAgent.readRate}% Read Rate ({senderBenchmarkSummary.topReadAgent.read} reads)
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', bgcolor: '#eff6ff' }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#dbeafe', color: '#1d4ed8', display: 'flex' }}>
                          <SendIcon />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 700 }}>
                            Highest Volume Sender
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e3a8a', noWrap: true }}>
                            {senderBenchmarkSummary.topVolumeAgent?.sender || 'N/A'}
                          </Typography>
                          {senderBenchmarkSummary.topVolumeAgent && (
                            <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 700 }}>
                              {senderBenchmarkSummary.topVolumeAgent.sent} dispatched messages
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        height: '100%',
                        bgcolor: senderBenchmarkSummary.topCtaAgent ? '#fff7ed' : '#f8fafc',
                        borderColor: senderBenchmarkSummary.topCtaAgent ? '#fed7aa' : undefined,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#ffedd5', color: '#c2410c', display: 'flex' }}>
                          <TouchAppIcon />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 700 }}>
                            Best CTA Conversion
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#9a3412', noWrap: true }}>
                            {senderBenchmarkSummary.topCtaAgent?.sender || 'No CTA clicks'}
                          </Typography>
                          {senderBenchmarkSummary.topCtaAgent && (
                            <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 700 }}>
                              {senderBenchmarkSummary.topCtaAgent.ctaRate}% Click Rate ({senderBenchmarkSummary.topCtaAgent.cta} clicks)
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>

                {/* 2. Interactive Agent Benchmark Comparison Graph with 3-Dot Switcher */}
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 2 }}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          📊 Agent Benchmark Comparison Graph
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            agentBenchmarkType === 'overall'
                              ? 'Overall Performance'
                              : agentBenchmarkType === 'read_rate'
                              ? 'Read-Through Leaderboard'
                              : agentBenchmarkType === 'delivery_rate'
                              ? 'Delivery & Reliability'
                              : agentBenchmarkType === 'cta_rate'
                              ? 'CTA & Response Rate'
                              : 'Volume & Dispatches'
                          }
                          sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 10, height: 22 }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {agentBenchmarkType === 'overall' && 'Side-by-side benchmark of read rates, delivery success, and response actions across agents and senders.'}
                        {agentBenchmarkType === 'read_rate' && 'Benchmark agent read-through performance to identify who captures the highest audience attention.'}
                        {agentBenchmarkType === 'delivery_rate' && 'Evaluate contact list validity and message delivery reliability per agent.'}
                        {agentBenchmarkType === 'cta_rate' && 'Measure actionable response rates and customer intent generated per agent.'}
                        {agentBenchmarkType === 'volume' && 'Benchmark total dispatches, delivered messages, and reads across agent workload.'}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <ToggleButtonGroup
                        size="small"
                        value={agentMainView}
                        exclusive
                        onChange={(_e, val) => val && setAgentMainView(val)}
                        sx={{ height: 30 }}
                      >
                        <ToggleButton value="graph" sx={{ px: 1.5, py: 0, fontSize: 11, fontWeight: 700, gap: 0.5 }}>
                          <BarChartIcon sx={{ fontSize: 15 }} /> Graph
                        </ToggleButton>
                        <ToggleButton value="table" sx={{ px: 1.5, py: 0, fontSize: 11, fontWeight: 700, gap: 0.5 }}>
                          <TableChartOutlinedIcon sx={{ fontSize: 15 }} /> Table
                        </ToggleButton>
                      </ToggleButtonGroup>

                      {/* 3-Dot Menu for Agent Benchmark */}
                      <IconButton
                        size="small"
                        onClick={(e) => setAgentMenuAnchor(e.currentTarget)}
                        sx={{ border: '1px solid #cbd5e1' }}
                        aria-label="Agent options"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={agentMenuAnchor}
                        open={Boolean(agentMenuAnchor)}
                        onClose={() => setAgentMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        MenuListProps={{ sx: { py: 0.5, minWidth: 220 } }}
                      >
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                          BENCHMARK METRIC
                        </Typography>
                        <MenuItem
                          selected={agentBenchmarkType === 'overall'}
                          onClick={() => {
                            setAgentBenchmarkType('overall');
                            setAgentMainView('graph');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          🌟 Overall Performance
                        </MenuItem>
                        <MenuItem
                          selected={agentBenchmarkType === 'read_rate'}
                          onClick={() => {
                            setAgentBenchmarkType('read_rate');
                            setAgentMainView('graph');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          📖 Read-Through Benchmark
                        </MenuItem>
                        <MenuItem
                          selected={agentBenchmarkType === 'delivery_rate'}
                          onClick={() => {
                            setAgentBenchmarkType('delivery_rate');
                            setAgentMainView('graph');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          🚚 Delivery & Reliability
                        </MenuItem>
                        <MenuItem
                          selected={agentBenchmarkType === 'cta_rate'}
                          onClick={() => {
                            setAgentBenchmarkType('cta_rate');
                            setAgentMainView('graph');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          🎯 CTA & Action Conversion
                        </MenuItem>
                        <MenuItem
                          selected={agentBenchmarkType === 'volume'}
                          onClick={() => {
                            setAgentBenchmarkType('volume');
                            setAgentMainView('graph');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          📊 Workload / Dispatches Volume
                        </MenuItem>

                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                          DISPLAY MODE
                        </Typography>
                        <MenuItem
                          selected={senderGraphMode === 'rate'}
                          onClick={() => {
                            setSenderGraphMode('rate');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          Rate (%)
                        </MenuItem>
                        <MenuItem
                          selected={senderGraphMode === 'count'}
                          onClick={() => {
                            setSenderGraphMode('count');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          Volume (#)
                        </MenuItem>

                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                          CHART TYPE
                        </Typography>
                        <MenuItem
                          selected={agentChartKind === 'bar'}
                          onClick={() => {
                            setAgentChartKind('bar');
                            setAgentMainView('graph');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          <BarChartIcon sx={{ fontSize: 18, mr: 1, color: '#1976d2' }} />
                          Bar Chart
                        </MenuItem>
                        <MenuItem
                          selected={agentChartKind === 'line'}
                          onClick={() => {
                            setAgentChartKind('line');
                            setAgentMainView('graph');
                            setAgentMenuAnchor(null);
                          }}
                        >
                          <ShowChartIcon sx={{ fontSize: 18, mr: 1, color: '#00a884' }} />
                          Line Chart
                        </MenuItem>
                      </Menu>
                    </Stack>
                  </Stack>

                  {agentMainView === 'graph' ? (
                  <Box sx={{ height: 300, width: '100%', mt: 1 }}>
                    {agentChartKind === 'line' ? (
                      <LineChart
                        height={290}
                        xAxis={[
                          {
                            scaleType: 'band',
                            data: senderBreakdown.map((s) => s.sender),
                            tickLabelStyle: { fontSize: 11, fontWeight: 600 },
                          },
                        ]}
                        yAxis={
                          senderGraphMode === 'rate' && agentBenchmarkType !== 'volume'
                            ? [{ max: 100, valueFormatter: (v) => `${v}%` }]
                            : undefined
                        }
                        series={
                          agentBenchmarkType === 'read_rate'
                            ? [
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.readRate : s.read)),
                                  label: senderGraphMode === 'rate' ? 'Read Rate %' : 'Read Count',
                                  color: '#00a884',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                              ]
                            : agentBenchmarkType === 'delivery_rate'
                            ? [
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.deliveryRate : s.delivered)),
                                  label: senderGraphMode === 'rate' ? 'Delivery Rate %' : 'Delivered Count',
                                  color: '#1976d2',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.failRate : s.failed)),
                                  label: senderGraphMode === 'rate' ? 'Failure Rate %' : 'Failed Count',
                                  color: '#64748b',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                              ]
                            : agentBenchmarkType === 'cta_rate'
                            ? [
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.ctaRate : s.cta)),
                                  label: senderGraphMode === 'rate' ? 'CTA Click Rate %' : 'CTA Clicks',
                                  color: '#ed6c02',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                              ]
                            : agentBenchmarkType === 'volume'
                            ? [
                                { data: senderBreakdown.map((s) => s.sent), label: 'Sent', color: '#3b82f6' },
                                { data: senderBreakdown.map((s) => s.delivered), label: 'Delivered', color: '#00a884' },
                                { data: senderBreakdown.map((s) => s.read), label: 'Read', color: '#9c27b0' },
                              ]
                            : [
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.readRate : s.read)),
                                  label: senderGraphMode === 'rate' ? 'Read Rate %' : 'Read Count',
                                  color: '#00a884',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.deliveryRate : s.delivered)),
                                  label: senderGraphMode === 'rate' ? 'Delivery Rate %' : 'Delivered Count',
                                  color: '#1976d2',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.ctaRate : s.cta)),
                                  label: senderGraphMode === 'rate' ? 'CTA Click Rate %' : 'CTA Clicks',
                                  color: '#ed6c02',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                              ]
                        }
                        margin={{ top: 20, right: 20, bottom: 40, left: 45 }}
                      />
                    ) : (
                      <BarChart
                        height={290}
                        borderRadius={5}
                        barLabel={(item) =>
                          item?.value != null
                            ? senderGraphMode === 'rate' && agentBenchmarkType !== 'volume'
                              ? `${Number(item.value).toFixed(0)}%`
                              : `${Number(item.value).toLocaleString()}`
                            : ''
                        }
                        xAxis={[
                          {
                            scaleType: 'band',
                            data: senderBreakdown.map((s) => s.sender),
                            tickLabelStyle: { fontSize: 11, fontWeight: 600 },
                          },
                        ]}
                        yAxis={
                          senderGraphMode === 'rate' && agentBenchmarkType !== 'volume'
                            ? [{ max: 100, valueFormatter: (v) => `${v}%` }]
                            : undefined
                        }
                        series={
                          agentBenchmarkType === 'read_rate'
                            ? [
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.readRate : s.read)),
                                  label: senderGraphMode === 'rate' ? 'Read Rate %' : 'Read Count',
                                  color: '#00a884',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                              ]
                            : agentBenchmarkType === 'delivery_rate'
                            ? [
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.deliveryRate : s.delivered)),
                                  label: senderGraphMode === 'rate' ? 'Delivery Rate %' : 'Delivered Count',
                                  color: '#1976d2',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.failRate : s.failed)),
                                  label: senderGraphMode === 'rate' ? 'Failure Rate %' : 'Failed Count',
                                  color: '#64748b',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                              ]
                            : agentBenchmarkType === 'cta_rate'
                            ? [
                                {
                                  data: senderBreakdown.map((s) => (senderGraphMode === 'rate' ? s.ctaRate : s.cta)),
                                  label: senderGraphMode === 'rate' ? 'CTA Click Rate %' : 'CTA Clicks',
                                  color: '#ed6c02',
                                  valueFormatter: (v) => (senderGraphMode === 'rate' ? `${v}%` : `${v}`),
                                },
                              ]
                            : agentBenchmarkType === 'volume'
                            ? [
                                { data: senderBreakdown.map((s) => s.sent), label: 'Sent', color: '#3b82f6' },
                                { data: senderBreakdown.map((s) => s.delivered), label: 'Delivered', color: '#00a884' },
                                { data: senderBreakdown.map((s) => s.read), label: 'Read', color: '#9c27b0' },
                                { data: senderBreakdown.map((s) => s.cta), label: 'CTA Clicks', color: '#ed6c02' },
                                { data: senderBreakdown.map((s) => s.failed), label: 'Failed', color: '#64748b' },
                              ]
                            : senderGraphMode === 'rate'
                            ? [
                                {
                                  data: senderBreakdown.map((s) => s.readRate),
                                  label: 'Read Rate %',
                                  color: '#00a884',
                                  valueFormatter: (v) => `${v}%`,
                                },
                                {
                                  data: senderBreakdown.map((s) => s.deliveryRate),
                                  label: 'Delivery Rate %',
                                  color: '#1976d2',
                                  valueFormatter: (v) => `${v}%`,
                                },
                                {
                                  data: senderBreakdown.map((s) => s.ctaRate),
                                  label: 'CTA Click Rate %',
                                  color: '#ed6c02',
                                  valueFormatter: (v) => `${v}%`,
                                },
                              ]
                            : [
                                {
                                  data: senderBreakdown.map((s) => s.sent),
                                  label: 'Dispatched (Sent)',
                                  color: '#3b82f6',
                                },
                                {
                                  data: senderBreakdown.map((s) => s.delivered),
                                  label: 'Delivered',
                                  color: '#00a884',
                                },
                                {
                                  data: senderBreakdown.map((s) => s.read),
                                  label: 'Read',
                                  color: '#9c27b0',
                                },
                                {
                                  data: senderBreakdown.map((s) => s.cta),
                                  label: 'CTA Clicks',
                                  color: '#ed6c02',
                                },
                                {
                                  data: senderBreakdown.map((s) => s.failed),
                                  label: 'Failed',
                                  color: '#64748b',
                                },
                              ]
                        }
                        margin={{ top: 20, right: 20, bottom: 40, left: 45 }}
                        sx={{
                          '& .MuiBarLabel-root': {
                            fill: '#ffffff',
                            fontSize: 10,
                            fontWeight: 700,
                            textShadow: '0 1px 2px rgba(0,0,0,0.85)',
                            pointerEvents: 'none',
                          },
                        }}
                      />
                    )}
                  </Box>
                  ) : (
                  <Box sx={{ mt: 1.5 }}>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Rank</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Sender / Agent</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Benchmark Rating</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Sent</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Delivered</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Read Rate</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>CTA Clicks</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Failed</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {senderBreakdown.map((row, idx) => (
                            <TableRow hover key={row.sender}>
                              <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 700, color: '#64748b' }}>
                                #{idx + 1}
                              </TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 700, color: '#0f172a' }}>
                                {row.sender}
                              </TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                <Chip
                                  size="small"
                                  label={row.benchmark}
                                  sx={{
                                    bgcolor: row.benchmarkBg,
                                    color: row.benchmarkColor,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    height: 22,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600 }}>{row.sent}</TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5, color: '#00a884', fontWeight: 600 }}>
                                {row.deliveryRate}% <span style={{ color: '#64748b', fontSize: 11 }}>({row.delivered})</span>
                              </TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5, color: '#9c27b0', fontWeight: 700 }}>
                                {row.readRate}% <span style={{ color: '#64748b', fontSize: 11 }}>({row.read})</span>
                              </TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5, color: '#ed6c02', fontWeight: 700 }}>
                                {row.ctaRate}% <span style={{ color: '#64748b', fontSize: 11 }}>({row.cta})</span>
                              </TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5, color: row.failed > 0 ? '#ef4444' : '#64748b', fontWeight: 600 }}>
                                {row.failRate}% <span style={{ color: '#94a3b8', fontSize: 11 }}>({row.failed})</span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  )}
                </Card>

                {/* 3. Comprehensive Agent Benchmark Table (MUI TableContainer) */}
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                    📋 Agent Performance & Benchmark Scorecard
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Individual performance rankings, engagement scores, and conversion breakdowns for each sender.
                  </Typography>

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Rank</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Sender / Agent</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Benchmark Rating</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Sent</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Delivered</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Read Rate</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>CTA Clicks</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#334155', py: 1.25, px: 1.5 }}>Failed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {senderBreakdown.map((row, idx) => (
                          <TableRow hover key={row.sender}>
                            <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 700, color: '#64748b' }}>
                              #{idx + 1}
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 700, color: '#0f172a' }}>
                              {row.sender}
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                              <Chip
                                size="small"
                                label={row.benchmark}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 11,
                                  color: row.benchmarkColor,
                                  bgcolor: row.benchmarkBg,
                                  border: `1px solid ${row.benchmarkColor}40`,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600 }}>
                              {row.sent}
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                {row.delivered}{' '}
                                <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>
                                  ({row.deliveryRate}%)
                                </span>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                              <Stack spacing={0.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#00a884', minWidth: 42 }}>
                                    {row.readRate}%
                                  </Typography>
                                  <Box sx={{ flex: 1, minWidth: 60, maxWidth: 100, height: 6, bgcolor: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                                    <Box
                                      sx={{
                                        width: `${Math.min(100, row.readRate)}%`,
                                        height: '100%',
                                        bgcolor: '#00a884',
                                        borderRadius: 3,
                                      }}
                                    />
                                  </Box>
                                </Stack>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                                  {row.read} of {row.delivered} delivered
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                                {row.cta}{' '}
                                <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>
                                  ({row.ctaRate}%)
                                </span>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, px: 1.5, color: '#64748b', fontWeight: 600 }}>
                              {row.failed}{' '}
                              {row.failed > 0 && (
                                <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>
                                  ({row.failRate}%)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Stack>
            )}

            {/* TAB 4: Recipient Logs Table */}
            {activeTab === 4 && (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField
                    size="small"
                    placeholder="Search contact or error..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ width: 220 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl size="small" sx={{ width: 140 }}>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="sent">Sent</MenuItem>
                      <MenuItem value="delivered">Delivered</MenuItem>
                      <MenuItem value="read">Read</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ width: 140 }}>
                    <Select value={senderFilter} onChange={(e) => setSenderFilter(e.target.value)}>
                      <MenuItem value="all">All Senders</MenuItem>
                      {senderBreakdown.map((s) => (
                        <MenuItem key={s.sender} value={s.sender}>
                          {s.sender}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto', maxHeight: 380 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#f8fafc', color: '#475569', fontWeight: 700, py: 1, px: 1.5 } }}>
                        <TableCell>Contact</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sender</TableCell>
                        <TableCell>Delivered At</TableCell>
                        <TableCell>Read At</TableCell>
                        <TableCell>Failure Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecipients.map((r, i) => (
                        <TableRow hover key={r.id || i} sx={{ '& td': { py: 1, px: 1.5, borderBottom: '1px solid #f1f5f9' } }}>
                          <TableCell sx={{ fontWeight: 600 }}>{r.contact || r.phone_number || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.status || 'unknown'}
                              sx={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'capitalize',
                                ...(r.status === 'read'
                                  ? { bgcolor: '#f3e8ff', color: '#7e22ce' }
                                  : r.status === 'delivered'
                                  ? { bgcolor: '#ecfdf5', color: '#047857' }
                                  : r.status === 'failed'
                                  ? { bgcolor: '#fee2e2', color: '#b91c1c' }
                                  : { bgcolor: '#eff6ff', color: '#1d4ed8' }),
                              }}
                            />
                          </TableCell>
                          <TableCell>{r.send_by || 'System'}</TableCell>
                          <TableCell sx={{ color: '#64748b' }}>
                            {r.delivered_at ? dateFormate(indianTimeZone(r.delivered_at), 'hh:mm A') : '—'}
                          </TableCell>
                          <TableCell sx={{ color: '#64748b' }}>
                            {r.read_at ? dateFormate(indianTimeZone(r.read_at), 'hh:mm A') : '—'}
                          </TableCell>
                          <TableCell sx={{ color: '#b91c1c', maxWidth: 180 }}>
                            {r.failure_reason ? formatFailureReasonText(r.failure_reason) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}
          </>
        )}
      </Box>
    </Card>
  );
}

